import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { portfolioService } from '../../services/portfolioService';
import { ApiError } from '../../services/errors';
export const safeUrl = (value, contact = false) => {
  try {
    const url = new URL(value);
    return (url.protocol === 'https:' || (contact && url.protocol === 'mailto:')) ? value : null;
  } catch (_) { return null; }
};
export default function Gallery({ title = 'Creative gallery', materialId, locale, revision, revisionError = false, onRevisionChange, onClose }) {
  const dialog = useRef();
  const request = useRef();
  const [page, setPage] = useState({ items: [], nextCursor: null, hasMore: false });
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async (cursor = null) => {
    request.current?.abort();
    if (!revision) {
      setPage({ items: [], nextCursor: null, hasMore: false });
      setError(false); setLoading(!revisionError); return;
    }
    if (!cursor) setPage({ items: [], nextCursor: null, hasMore: false });
    const controller = new AbortController(); request.current = controller;
    let refreshingRevision = false;
    setLoading(true); setError(false);
    try {
      const data = await portfolioService.gallery(materialId, locale, cursor, controller.signal);
      if (data.contentRevision !== revision) {
        throw new ApiError('Gallery revision changed.', { code: 'CONTENT_REVISION_CHANGED' });
      }
      if (!controller.signal.aborted) setPage((old) => ({ ...data,
        items: cursor ? [...old.items, ...data.items] : data.items }));
    } catch (failure) {
      if (controller.signal.aborted) return;
      if (failure.code === 'CONTENT_REVISION_CHANGED') {
        setPage({ items: [], nextCursor: null, hasMore: false });
        if (onRevisionChange) { refreshingRevision = true; onRevisionChange(); }
        else setError(true);
      } else setError(true);
    } finally { if (!controller.signal.aborted && !refreshingRevision) setLoading(false); }
  }, [materialId, locale, revision, revisionError, onRevisionChange]);
  useEffect(() => {
    const opener = document.activeElement;
    dialog.current?.querySelector('button')?.focus();
    return () => {
      request.current?.abort();
      const currentOpener = opener?.isConnected ? opener : document.getElementById(opener?.id);
      (currentOpener || document.getElementById('portfolio-main'))?.focus();
    };
  }, []);
  useEffect(() => { load(); return () => request.current?.abort(); }, [load]);
  const onKeyDown = (event) => {
    if (event.key === 'Escape') onClose();
    if (event.key === 'Tab') {
      const controls = [...dialog.current.querySelectorAll('button:not(:disabled), a[href]')];
      const first = controls[0]; const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  };
  const unavailable = error || revisionError;
  const busy = loading && !revisionError;
  return createPortal(<div className="portfolio-page portfolio-gallery-backdrop"><div ref={dialog} role="dialog"
    aria-modal="true" aria-labelledby="gallery-title" onKeyDown={onKeyDown}
    className="portfolio-gallery" aria-busy={busy}>
    <button onClick={onClose}>Close gallery</button><h2 id="gallery-title">{title}</h2>
    {page.items.map((asset) => safeUrl(asset.url) && <figure key={asset.id}>
      <img src={asset.url} alt={asset.alt} loading="lazy" />
      {asset.generated && <figcaption>AI-generated creative work</figcaption>}
    </figure>)}
    {!busy && !unavailable && !page.items.length && <p>No published gallery items yet.</p>}
    {busy && <p role="status">Loading gallery…</p>}
    {unavailable && <div role="status">Gallery unavailable. <button onClick={() =>
      revisionError && onRevisionChange ? onRevisionChange() : load(page.nextCursor)}>
      Retry gallery</button></div>}
    {page.hasMore && !unavailable && <button disabled={busy} onClick={() => load(page.nextCursor)}>
      Load more</button>}
  </div></div>, document.body);
}
